import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import { Form, Input } from 'antd';
import s from './s.module.less';
import StateSelect from 'components/form/StateSelect';
import { CompanyInfo } from 'types/paymentSetup';
import AutoAddressInput from 'components/AutoAddressInput';
import useSettings from 'hooks/useSettings';
import { sortByProperty } from 'utils/form';
import { EnumFields } from 'types/enumerationData';

interface BusinessFormProps {
    value: CompanyInfo;
    onChange: (changedData: Partial<CompanyInfo>) => void;
}

const BusinessForm: React.FC<BusinessFormProps> = forwardRef(({ value, onChange }, ref) => {
    const namePath = (fieldName: keyof CompanyInfo) => [fieldName];
    const [form] = Form.useForm();
    const [setting] = useSettings();
    const stateOption = sortByProperty(setting[EnumFields.STATE] || [], 'label');

    useEffect(() => {
        form.setFieldsValue(value);
    }, [value, form]);

    const handleValuesChange = (changedValues: Partial<CompanyInfo>, allValues: Partial<CompanyInfo>) => {
        onChange({ ...allValues, ...changedValues });
    };

    useImperativeHandle(ref, () => ({
        validateFields: () => {
            return form.validateFields();
        },
    }));

    if (!setting || !setting[EnumFields.STATE]) {
        return null;
    }

    return (
        <div className={s.businessFormContainer}>
            <h3 className={s.sectionTitle}>Company details</h3>
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>

                <Form.Item
                    label="Company name"
                    name={namePath('companyName')}
                    rules={[{ required: true, message: 'Please enter the company name' }]}
                >
                    <Input placeholder="Legal name of the company" />
                </Form.Item>

                <Form.Item
                    label="Company address"
                    name={namePath('address')}
                    rules={[{ required: true, message: 'Please enter the company address' }]}
                >
                    <AutoAddressInput
                        placeholder="Street address"
                        addCityToAddress
                        onStateChange={(val) => {
                            const targetState = stateOption.find((item) => item.value === val || item.label === val);
                            if (targetState) {
                                form.setFieldsValue({
                                    state: targetState.value,
                                });
                            }
                        }}
                        onCityChange={(val) => {
                            form.setFieldsValue({
                                city: val,
                            });
                        }}
                        onPostcodeChange={(val) => {
                            form.setFieldsValue({
                                zip: val,
                            });
                        }}
                    />
                </Form.Item>

                <div className={s.formRow}>
                    <Form.Item
                        label="City"
                        name={namePath('city')}
                        rules={[{ required: true, message: 'Please enter the city' }]}
                        className={s.formColCity}
                    >
                        <Input placeholder="City" />
                    </Form.Item>

                    <Form.Item
                        label="State"
                        name={namePath('state')}
                        rules={[{ required: true, message: 'Please select the state' }]}
                        className={s.formColState}
                    >
                        <StateSelect />
                    </Form.Item>

                    <Form.Item
                        label="ZIP code"
                        name={namePath('zip')}
                        rules={[{ required: true, message: 'Please enter the ZIP code' }]}
                        className={s.formColZip}
                    >
                        <Input placeholder="ZIP code" />
                    </Form.Item>
                </div>

                <Form.Item
                    label="Company Tax ID"
                    name={namePath('taxId')}
                    rules={[{ required: true, message: 'Please enter the Company Tax ID' }]}
                    extra="Enter the 9-digit Employer Identification Number (EIN) issued by the IRS."
                >
                    <Input />
                </Form.Item>
            </Form>
        </div>
    );
});

export default BusinessForm;
